/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-common
 * (C) Copyright IBM Corp. 2020
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { ClickableTile as Tile } from 'carbon';
import Button from '@ba-ui-toolkit/button';
import SVGIcon from '@ba-ui-toolkit/svgicon';
import TruncatedText from '@ba-ui-toolkit/truncatedtext';
import Multilinetruncatedtext from '@ba-ui-toolkit/multilinetruncatedtext';
import Tags from '@ba-ui-toolkit/tags';
import FlexLayout, { FlexItem } from '@ba-ui-toolkit/flexlayout';
import Label from '@ba-ui-toolkit/label';
import ToolTip from '@ba-ui-toolkit/tooltip';
import verticalMenuOverflow from '@ba-ui-toolkit/ba-graphics/dist/icons-js/overflow-menu--horizontal_16';
import folder16 from '@ba-ui-toolkit/ba-graphics/dist/icons-js/folder_16';
import { classnames as cx, propsUtils } from '@ba-ui-toolkit/utils';
import styles from './CarbonTile.scss';

const tileClassName = styles.tileClassName.replace(/"/g, '');
const { sanitize } = propsUtils;

export default class CarbonTile extends Component {
    static propTypes = {
        /** Custom class name(s) */
        className: PropTypes.string,
        /** Select folder or asset tile */
        container: PropTypes.bool,
        /** Flag to select the tile */
        selected: PropTypes.bool,
        /** Data passed to tile */
        content: PropTypes.shape({
            type: PropTypes.string,
            iconId: PropTypes.string,
            label: PropTypes.string,
            sub_label: PropTypes.string,
            date: PropTypes.string,
            showTag: PropTypes.bool,
            value: PropTypes.any,
        }).isRequired,
        /** Callback function for context menu */
        onMenu: PropTypes.func,
        onAction: PropTypes.func,
        /** Svg icon when loading context menu */
        ellipsisIcon: PropTypes.element,
        /** ellipsisIcon toop string */
        ellipsisTooltipLabel: PropTypes.string,
        /** Data Label to display in footer */
        dateLabel: PropTypes.string,
        /** boolean to specify tile background color  */
        light: PropTypes.bool
    };

    static defaultProps = {
        size: 'small',
        container: false,
        selected: false,
        className: '',
        ellipsisTooltipLabel: 'Action menu',
        dateLabel: 'Last modified',
    }

    state = {
        hover: false,
    }


    _onContextMenu = (e) => {
        const { onMenu } = this.props;
        onMenu && onMenu(e);
    }

    _onClick = (e) => {
        const { onAction } = this.props;
        onAction && onAction(e);
    }

    _onMouseEnter = () => {
		this.setState({ hover: true });
	};

	_onMouseLeave = () => {
		this.setState({ hover: false });
	};

    _ref = (node) => {
        this._buttonNode = node;
    }

    _menuRenderer() {
        const { ellipsisIcon } = this.props;
        let menu;
        menu = (this.state.hover === true) ? (
            <ToolTip
                className={`${tileClassName}__header__title`}
                orient='bottom'
                title={this.props.ellipsisTooltipLabel}
                delay
            >
                {ellipsisIcon || <Button
                    className={`${tileClassName}__header__icon`}
                    ref={this._ref}
                    onClick={this._onContextMenu}
                    variant='inline'
                    iconSize='small'
                    icon={verticalMenuOverflow.id}
                />}
            </ToolTip>
            ): null;
        return menu;
    }


    render() {
        const {
            container, 
            content, 
            className, 
            selected, 
            onMenu, 
            ellipsisIcon, 
            size, 
            dateLabel,
            onAction, 
            light,
            ...others } = this.props;

        const classNames = cx(
            tileClassName, 
            size, 
            {
                selected,
                container,
            }
        );

        const presetColors = ['red', 'magenta', 'purple', 'blue', 'cyan', 'teal', 'green', 'gray', 'coolgray', 'warmgray'];
        const tagColor = presetColors.indexOf(content.color) !== -1 ? content.color : 'coolgray';

        if (container) {
            return (
                <Tile
                    clicked = {false}
                    handleClick={onAction ? this._onClick : null}
                    handleKeyDown={onAction ? this._onClick : null}
                    onContextMenu={onMenu ? this._onContextMenu : null}
                    onMouseEnter= { this._onMouseEnter }
                    onMouseLeave= { this._onMouseLeave }
                    href="javascript:void(0)"
                    light={light}
                    className={`${classNames} ${className}`}
                    {...sanitize(others, CarbonTile)}
                >
                    <FlexLayout direction = 'column' justifyContent='flex-start' className={`${tileClassName}__content`}>
                        <FlexItem grow shrink className={`${tileClassName}__body`}>
                            <FlexLayout direction = 'row' 
                                justifyContent='space-between' 
                                alignItems='flex-start'
                            >
                                <TruncatedText className={`${tileClassName}__body__label`}
                                    tooltipDelay
                                    title={content.label}
                                    width={'100%'}
                                    style={{ maxWidth: '100%' }}
                                    value={content.label}
                                	location={'end'}
                                />
                                <FlexItem>{ this.state.hover ? (onMenu || ellipsisIcon) && this._menuRenderer() : null }</FlexItem>
                            </FlexLayout>
                        </FlexItem>
                        <FlexLayout direction = 'row' justifyContent='flex-start'>
                            <FlexItem className={`${tileClassName}__folder__label`}>
                                <Label label = {dateLabel}></Label>
                            </FlexItem>
                            <FlexItem className={`${tileClassName}__folder__date`} >
                                <TruncatedText
                                    tooltipDelay 
                                    title={content.date} 
                                    width={'100%'} 
                                    value={content.date} />
                            </FlexItem>
                            <FlexItem className={`${tileClassName}__folder__icon`}>
                                <SVGIcon className={`${tileClassName}__right`} 
                                    iconId={content.iconId ? content.iconId : folder16.id } 
                                    title={content.type} 
                                    style ={{ fill: 'black' }} 
                                />
                            </FlexItem>
                        </FlexLayout>
                    </FlexLayout>
                    
                </Tile>
            );
        }
        return (
            <Tile 
                handleClick={onAction ? this._onClick : null}
                handleKeyDown={onAction ? this._onClick : null}
                onContextMenu={onMenu ? this._onContextMenu : null}
                onMouseEnter= { this._onMouseEnter }
                onMouseLeave= { this._onMouseLeave }
                href="javascript:void(0)"
                light={light}
                className={`${classNames} ${className}`}
                {...sanitize(others, CarbonTile)}
            >
                <FlexLayout direction = 'column' justifyContent='flex-start' className={`${tileClassName}__content`}>
                    <FlexItem grow shrink className={`${tileClassName}__body`}>
                        <FlexLayout direction = 'row' 
                            justifyContent='space-between' 
                            alignItems='flex-start'
                            width='100%'
                        >
                        	<FlexItem grow={true} shrink={true}>
	                            <Multilinetruncatedtext className={`${tileClassName}__body__label ${content.sub_label ? 'has-sublabel' : ''}`}
	                                tooltipDelay
	                                title={content.label}
	                                style={{ width: '100%', whiteSpace: 'nowrap' }}
	                                value={content.label}
	                            	numLines={ content.sub_label ? 2 : 3 }
	                            />
                            </FlexItem>
                            <FlexItem>{ this.state.hover ? (onMenu || ellipsisIcon) && this._menuRenderer() : null }</FlexItem>
                        </FlexLayout>
                        <FlexLayout direction='row' 
                            justifyContent='space-between' 
                            alignItems='center' 
                        >
                            { content.sub_label ? 
	                      		<Multilinetruncatedtext className={`${tileClassName}__body__sublabel`}
		                            tooltipDelay
		                            title={content.sub_label}
		                            style={{ width: '100%', whiteSpace: 'nowrap', maxWidth: '100%' }}
		                            value={content.sub_label}
		                			numLines={2}
		                        />
                                : null
                            }
                            
                        </FlexLayout>
                    </FlexItem>
                    <FlexItem className={`${tileClassName}__footer`} >
                        <FlexLayout direction = 'row' justifyContent='flex-end'>
                            <FlexItem className={`${tileClassName}__footer__label`}>
                                <Label label = {dateLabel}></Label>
                                <br/><TruncatedText className={`${tileClassName}__footer__date`} 
                                    tooltipDelay 
                                    title={content.date} 
                                    width={'100%'} 
                                    value={content.date} 
                                />
                            </FlexItem>
                            <FlexItem className={`${tileClassName}__footer__tag`}>
                                {
                                    content.showTag === true ? 
                                    <Tags tags={[{ value: content.type.toLowerCase(), label: content.type.toLowerCase(), color: tagColor }]} removable={false}/>: <div></div>
                                }
                            </FlexItem>
                            <FlexItem className={`${tileClassName}__right`}>
                                <SVGIcon className={`${tileClassName}__right`} 
                                    iconId={content.iconId ? content.iconId : folder16.id} 
                                    title={content.type} 
                                    size='large' 
                                    style ={{ fill: tagColor }} />
                            </FlexItem>
                        </FlexLayout>
                    </FlexItem>
                </FlexLayout>
                
            </Tile>
        );
    }
}
